import "./RequestQuote.css";
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Button, Modal, Form } from "react-bootstrap";
import DataTable from "react-data-table-component";
import Loading from "react-fullscreen-loading";
import { FaArrowRight, FaPlus } from "react-icons/fa";
import UserDashboardLayout from "../../../Components/UserDashboardLayout/UserDashboardLayout";
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import MapProduct from "../../../utils/ProductMapper/MapProduct";
import LicenseSelector from "../../../Components/LicenseSelector/LicenseSelector";
import PlanSelector, { ExpiryNotice, PlanUpsell } from "../../../Components/PlanSelector/PlanSelector";
import {
  planByPayload,
  toPlanPayload,
  money,
  savingVsMonthly,
  isCustomPlan,
  DEFAULT_PLAN,
} from "../../../Components/PlanSelector/plans";
import PlatformTiles from "../../../Components/QuoteForm/PlatformTiles";
import QuantityStepper from "../../../Components/QuoteForm/QuantityStepper";
import { SLA_LABEL } from "../../../config/sla";

const QUOTE_STATUS_OPTIONS = {
  pending: "Pending",
  payment_link_sent: "Payment Link Sent",
  completed: "Completed",
  cancelled: "Cancelled",
};

const statusStyle = (value) => {
  const map = {
    pending: { backgroundColor: "#fef3c7", color: "#92400e" },
    payment_link_sent: { backgroundColor: "#dbeafe", color: "#1e40af" },
    completed: { backgroundColor: "#dcfce7", color: "#166534" },
    cancelled: { backgroundColor: "#fee2e2", color: "#991b1b" },
  };
  const c = map[value] || map.pending;
  return {
    ...c,
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: "0.75rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  };
};

const typeBadgeStyle = (isExtend) => ({
  backgroundColor: isExtend ? "#ede9fe" : "#e0f2fe",
  color: isExtend ? "#5b21b6" : "#075985",
  padding: "2px 10px",
  borderRadius: 12,
  fontSize: "0.75rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
});

const emptyForm = {
  mode: "new",
  // A renewal covers any number of the caller's licenses; the seat count is
  // derived from this list rather than typed, because renewing "3 seats" while
  // naming 2 licenses is not a thing anyone can act on.
  selectedLicenses: [],
  product: "",
  plan: toPlanPayload(DEFAULT_PLAN),
  // Seat count for NEW requests only. Previously hardcoded to 1 on submit,
  // which made multi-seat requests impossible from this screen even though the
  // API has always taken it.
  licenses: 1,
  message: "",
};

export default function RequestQuote() {
  const location = useLocation();
  const navigate = useNavigate();

  const [screenloader, setScreenloader] = useState(true);
  const [pending, setPending] = useState(true);
  const [quotes, setQuotes] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  // Errors are shown on the offending control rather than as a toast: a toast
  // names a problem off to the side, a red field points at what needs a click.
  const [platformError, setPlatformError] = useState(false);
  const [messageError, setMessageError] = useState(false);
  // What to restore if the user picks custom pricing and then changes their mind.
  const lastPricedPlan = useRef(toPlanPayload(DEFAULT_PLAN));
  const platformRef = useRef(null);
  const messageRef = useRef(null);

  const fetchQuotes = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/quote/mine`, {
        headers: { "access-token": token },
      });
      if (response.data?.status) {
        setQuotes(response.data.quotes || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load your quote requests");
    } finally {
      setScreenloader(false);
      setPending(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  // Opened from a "Renew" button elsewhere in the portal (carries router
  // state). The full license objects are passed through so the selector can
  // show them preselected without an extra fetch.
  //
  // `extendLicenses` (plural) is the licenses listing's bulk renew; the
  // singular `extendLicense` is still honoured for the per-row and per-card
  // buttons that send one.
  // Opened from a renewal email: /User/RequestQuote?renew=12,34. The email can
  // only carry ids, so the licenses are fetched through the caller's own
  // scoped index — an id that isn't theirs (forwarded email, other account
  // signed in) simply doesn't come back. Whatever does come back opens the
  // same preselected renewal form as a "Renew" button would.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const renew = params.get("renew");
    if (!renew) return;

    const ids = renew.split(",").map(Number).filter(Number.isInteger);
    // Drop the query first so a refresh / back doesn't reopen the modal.
    navigate(location.pathname, { replace: true, state: {} });
    if (!ids.length) return;

    (async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/license/index`, {
          params: { ids: ids.join(","), limit: ids.length, page: 1 },
          headers: { "access-token": localStorage.getItem("accessToken") },
        });
        const found = response.data?.status ? response.data.licenses || [] : [];
        if (!found.length) {
          toast.error("We couldn't find those licenses on this account. Pick the ones to renew below.");
          return;
        }
        if (found.length < ids.length) {
          toast.error("Some licenses from the email aren't on this account and were left out.");
        }
        setForm({ ...emptyForm, mode: "extend", selectedLicenses: found });
        setShowModal(true);
      } catch (error) {
        toast.error("Couldn't load the licenses to renew. Please pick them below.");
      }
    })();
    // Runs once on arrival; the query is cleared above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const many = location.state?.extendLicenses;
    const one = location.state?.extendLicense;
    const preselected = Array.isArray(many) && many.length ? many : (one ? [one] : []);

    if (preselected.length) {
      setForm({
        ...emptyForm,
        mode: "extend",
        selectedLicenses: preselected,
      });
      setShowModal(true);
      // Clear the state so a refresh / back doesn't reopen the modal.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const openNew = () => {
    setForm({ ...emptyForm, mode: "new" });
    setShowModal(true);
  };

  const openExtend = () => {
    setForm({ ...emptyForm, mode: "extend" });
    setShowModal(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setShowModal(false);
    setForm(emptyForm);
    setPlatformError(false);
    setMessageError(false);
  };

  const handleChange = (e) => {
    // Destructured up front because React 17 pools synthetic events — reading
    // e.target inside the updater below can yield a released event.
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isExtend = form.mode === "extend";
  const selectedLicenses = form.selectedLicenses;
  // A single selection still drives the expiry-aware copy (ExpiryNotice, the
  // "to 17 Sep 2027" labels on the plan cards, PlanUpsell). With several
  // licenses those have no single answer, so they are suppressed rather than
  // shown for an arbitrary one of the set — a wrong date is worse than none.
  const selectedLicense = selectedLicenses.length === 1 ? selectedLicenses[0] : null;
  const selectedPlan = planByPayload(form.plan);
  const custom = isCustomPlan(form.plan);
  // Renewals count the licenses picked; new requests use the stepper.
  const licenses = isExtend ? selectedLicenses.length : form.licenses;
  const total = selectedPlan ? selectedPlan.priceUsd * licenses : 0;
  const savings = selectedPlan ? savingVsMonthly(selectedPlan) * licenses : 0;

  const setLicenses = (next) => setForm((prev) => ({ ...prev, licenses: next }));

  // Picking custom swaps the summary into "describe it and send"; picking any
  // priced plan puts it straight back, so leaving is a single click.
  const choosePlan = (next) => {
    if (!isCustomPlan(next)) lastPricedPlan.current = next;
    setMessageError(false);
    setForm((prev) => ({ ...prev, plan: next }));
    if (isCustomPlan(next)) setTimeout(() => messageRef.current?.focus(), 0);
  };

  const leaveCustom = () => {
    setMessageError(false);
    setForm((prev) => ({ ...prev, plan: lastPricedPlan.current }));
  };

  const switchMode = (mode) => {
    setPlatformError(false);
    setMessageError(false);
    // A renewal has no custom option (PlanSelector disallows it there), so drop
    // back to the last priced plan rather than carrying custom across.
    const plan = mode === "extend" && custom ? lastPricedPlan.current : form.plan;
    setForm({ ...emptyForm, mode, plan });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("accessToken");

    if (!form.plan) {
      toast.error("Please select a plan");
      return;
    }

    // Platform and the custom-plan message are marked on the controls
    // themselves; the license selector still toasts because it is a single
    // field with nowhere obvious to put inline text.
    const missingPlatform = form.mode === "new" && !form.product;
    const missingMessage = custom && !form.message.trim();
    setPlatformError(missingPlatform);
    setMessageError(missingMessage);

    if (missingPlatform) {
      // Optional call: not every environment implements scrollIntoView.
      platformRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      return;
    }
    if (missingMessage) {
      messageRef.current?.focus();
      return;
    }
    if (isExtend && selectedLicenses.length === 0) {
      toast.error("Select at least one license to renew");
      return;
    }

    setSubmitting(true);
    try {
      let response;
      if (isExtend) {
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/quote/extend`,
          {
            license_ids: selectedLicenses.map((l) => l.id),
            plan: form.plan,
            message: form.message,
            // Deliberately not sent: the server derives the seat count from the
            // licenses it has verified as the caller's own, so the price cannot
            // be talked down by a client claiming fewer seats than it named.
          },
          { headers: { "access-token": token } }
        );
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/quote/create`, {
          email: localStorage.getItem("email"),
          product: form.product,
          plan: form.plan,
          message: form.message,
          no_of_licenses: licenses,
          // name / phone / country / organisation are omitted on purpose —
          // createQuote() backfills them from the Users row, and sending ""
          // here would overwrite good account data with empty strings.
        });
      }

      if (response.data.status === 1) {
        toast.success(
          isExtend
            ? `Renewal request submitted for ${licenses} license${licenses === 1 ? "" : "s"}!`
            : "Quote request submitted successfully!"
        );
        setShowModal(false);
        setForm(emptyForm);
        setPlatformError(false);
        setMessageError(false);
        setPending(true);
        fetchQuotes();
      } else {
        toast.error(response.data.message || "Failed to submit request");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      name: "Type",
      sortable: false,
      minWidth: "110px",
      cell: (row) => {
        const isExtend = row.request_type === "extend";
        return <span style={typeBadgeStyle(isExtend)}>{isExtend ? "Renewal" : "New"}</span>;
      },
    },
    {
      name: "Product",
      selector: (row) => row.product,
      minWidth: "200px",
      cell: (row) => <div style={{ whiteSpace: "pre-wrap" }}>{row.product ? MapProduct(row.product, "simple") : "-"}</div>,
    },
    {
      // A renewal can cover several licenses, so this lists all of them.
      // `licenses` comes from RequestQuoteLicenses and is the authority; the
      // license_id fallback keeps rows created before that table existed
      // rendering exactly as they did.
      name: "Licenses",
      minWidth: "220px",
      cell: (row) => {
        const attached = Array.isArray(row.licenses) && row.licenses.length
          ? row.licenses
          : (row.license_id
            ? [{
              id: row.license_id,
              license_reference_id: row.license_reference_id,
              expires_at: row.license_expires_at,
            }]
            : []);

        if (!attached.length) return <span style={{ color: "#94a3b8" }}>—</span>;

        return (
          <div style={{ whiteSpace: "pre-wrap", padding: "4px 0" }}>
            {attached.length > 1 && (
              <div style={{ color: "#5b21b6", fontWeight: 600, fontSize: "0.75rem", marginBottom: 2 }}>
                {attached.length} licenses
              </div>
            )}
            {attached.map((l) => (
              <div key={l.id}>
                <span>{l.license_reference_id || `#${l.id}`}</span>
                {l.expires_at && (
                  <small style={{ color: "#94a3b8" }}> · expires {ShowTime(l.expires_at)}</small>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      name: "Plan",
      selector: (row) => row.plan,
      minWidth: "160px",
      cell: (row) => <div style={{ whiteSpace: "pre-wrap" }}>{row.plan || "-"}</div>,
    },
    {
      name: "Message",
      selector: (row) => row.message,
      minWidth: "180px",
      cell: (row) => <div style={{ whiteSpace: "pre-wrap" }}>{row.message || "-"}</div>,
    },
    {
      name: "Status",
      minWidth: "150px",
      cell: (row) => <span style={statusStyle(row.status)}>{QUOTE_STATUS_OPTIONS[row.status] || "Pending"}</span>,
    },
    {
      name: "Payment",
      minWidth: "140px",
      cell: (row) => {
        switch (row.status) {
          case "completed":
            return <span style={{ color: "#16a34a" }}>Done</span>;
          case "cancelled":
            return <span style={{ color: "#991b1b" }}>Cancelled</span>;
          default:
            return row.payment_link ? (
              <Button
                size="sm"
                variant="primary"
                href={row.payment_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Pay Now <FaArrowRight size={11} />
              </Button>
            ) : (
              <span style={{ color: "#94a3b8" }}>Awaiting link</span>
            );
        }
      },
    },
    {
      name: "Requested",
      selector: (row) => row.created_at,
      minWidth: "160px",
      cell: (row) => <div style={{ whiteSpace: "pre-wrap" }}>{ShowTime(row.created_at)}</div>,
    },
  ];

  return (
    <div>
      <UserDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">
            <div className="row align-items-center mb-3">
              <div className="col">
                <h2 className="mb-1">Request a Quote</h2>
                <p style={{ color: "#64748b", marginBottom: 0 }}>
                  Request a new license or extend an existing one. Previous requests are shown below — they're read-only.
                </p>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col d-flex flex-wrap gap-2">
                <Button variant="primary" className="admin-btn" onClick={openNew}>
                  <FaPlus size={12} /> Request New Quote
                </Button>
                <Button variant="outline-primary" onClick={openExtend}>
                  Extend a License
                </Button>
              </div>
            </div>

            <div className="table-wrapper">
              <DataTable
                columns={columns}
                data={quotes}
                dense
                direction="auto"
                highlightOnHover
                noHeader
                persistTableHead
                responsive
                progressPending={pending}
                progressComponent={<CustomLoader />}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50]}
                noDataComponent={<div style={{ padding: 24, color: "#64748b" }}>You haven't requested any quotes yet.</div>}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Wider and body-scrolling: the form carries a 3-across platform grid,
          the plan cards, a stepper and a price summary, which is more than the
          default 500px modal holds without the footer scrolling off.
          `quote-modal` is what makes `scrollable` actually work here — see
          RequestQuote.css, the <Form> between .modal-content and .modal-body
          breaks Bootstrap's flex chain. */}
      <Modal show={showModal} onHide={closeModal} centered size="lg" scrollable dialogClassName="quote-modal">
        <Modal.Header closeButton>
          <div>
            <Modal.Title>
              {isExtend
                ? (licenses > 1 ? `Renew ${licenses} licenses` : "Renew your license")
                : "Request a New Quote"}
            </Modal.Title>
            {isExtend && (
              <div style={{ color: "#64748b", fontSize: "0.9rem", marginTop: 2 }}>
                Time is added to your current expiry, not from today.
              </div>
            )}
          </div>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Request type</Form.Label>
              <Form.Select
                name="mode"
                value={form.mode}
                onChange={(e) => switchMode(e.target.value)}
              >
                <option value="new">New license</option>
                <option value="extend">Extend an existing license</option>
              </Form.Select>
            </Form.Group>

            {isExtend ? (
              <>
                {selectedLicense?.expires_at && (
                  <div className="mb-3">
                    <ExpiryNotice expiresAt={selectedLicense.expires_at} />
                  </div>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Licenses to renew*</Form.Label>
                  <LicenseSelector
                    isMulti
                    placeholder="Search your licenses by id..."
                    value={selectedLicenses}
                    onChange={(list) =>
                      setForm((prev) => ({ ...prev, selectedLicenses: list || [] }))
                    }
                  />
                  <Form.Text muted>
                    Pick as many as you like — they'll be renewed on one invoice.
                  </Form.Text>
                </Form.Group>

                {selectedLicenses.length > 0 && (
                  <ul className="renewal-license-list">
                    {selectedLicenses.map((l) => (
                      <li key={l.id}>
                        <span className="renewal-license-ref">{l.license_reference_id || `#${l.id}`}</span>
                        <span className="renewal-license-meta">
                          {MapProduct(l.product, "text")}
                          {l.expires_at && <> · expires {ShowTime(l.expires_at)}</>}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label style={platformError ? { color: "#b91c1c" } : undefined}>
                  CAD platform*
                </Form.Label>
                <PlatformTiles
                  innerRef={platformRef}
                  value={form.product}
                  invalid={platformError}
                  onChange={(code) => {
                    setPlatformError(false);
                    setForm((prev) => ({ ...prev, product: code }));
                  }}
                />
                {platformError && (
                  <p className="quote-field-error" role="alert">
                    Choose your CAD platform to continue.
                  </p>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>{form.mode === "extend" ? "Extend by" : "Plan*"}</Form.Label>
              <PlanSelector
                mode={form.mode === "extend" ? "extend" : "new"}
                // Only a new licence can be custom-priced. A renewal extends an
                // existing term, so "tell us what you need" has no meaning there
                // — PlanSelector enforces the same rule.
                allowCustom={form.mode === "new"}
                value={form.plan}
                onChange={choosePlan}
                currentExpiry={selectedLicense?.expires_at}
              />
              {form.mode === "extend" && selectedLicense?.expires_at && (
                <PlanUpsell value={form.plan} currentExpiry={selectedLicense.expires_at} />
              )}
            </Form.Group>

            <div className="d-flex align-items-center justify-content-between mb-3">
              <Form.Label className="mb-0">Licenses</Form.Label>
              {isExtend ? (
                /* Not editable on a renewal: the count IS the number of
                   licenses picked above, so a stepper here could only ever
                   disagree with the list the invoice is built from. */
                <span className="renewal-license-count">
                  {licenses} selected
                </span>
              ) : (
                <QuantityStepper value={licenses} onChange={setLicenses} />
              )}
            </div>

            {custom ? (
              <>
                {/* No amount anywhere in this branch: custom pricing has no
                    price until someone quotes it, and showing a number we
                    cannot stand behind is worse than showing none. */}
                <Form.Group className="mb-3">
                  <Form.Label>Tell us what you need*</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="message"
                    ref={messageRef}
                    value={form.message}
                    onChange={(e) => {
                      // Read before the updater runs: React 17 pools synthetic
                      // events, so e.target inside a deferred callback is unsafe.
                      const value = e.target.value;
                      setMessageError(false);
                      setForm((prev) => ({ ...prev, message: value }));
                    }}
                    style={messageError ? { borderColor: "#fca5a5" } : undefined}
                    placeholder="How many licenses, which term, any invoicing or procurement requirements."
                  />
                  {messageError && (
                    <p className="quote-field-error" role="alert">
                      Tell us what you need so we can quote it.
                    </p>
                  )}
                </Form.Group>

                <div className="quote-summary">
                  <div className="quote-summary-title">
                    Custom quote · {licenses} {licenses === 1 ? "license" : "licenses"}
                  </div>
                  <p className="quote-summary-note">We'll price this and send your invoice.</p>
                </div>

                <div className="text-center">
                  <Button variant="link" size="sm" onClick={leaveCustom} className="p-0">
                    Back to the standard plans
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Message (optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="A custom term, or anything else we should know?"
                  />
                </Form.Group>

                <div className="quote-summary">
                  {/* A renewal has no total until licenses are picked, and "$0"
                      reads as free rather than as "nothing selected yet". */}
                  {isExtend && licenses === 0 ? (
                    <p className="quote-summary-note">
                      Pick the licenses you want to renew to see your total.
                    </p>
                  ) : (
                    <div className="quote-summary-row">
                      <div>
                        <div className="quote-summary-title">
                          {selectedPlan ? selectedPlan.label : "—"} · {licenses} {licenses === 1 ? "license" : "licenses"}
                        </div>
                        {savings > 0 && (
                          <p className="quote-summary-note is-saving">
                            Saves {money(savings)} against monthly
                          </p>
                        )}
                      </div>
                      <div className="quote-summary-amount">{money(total)}</div>
                    </div>
                  )}
                </div>
              </>
            )}

          </Modal.Body>
          <Modal.Footer className="d-block">
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={closeModal} disabled={submitting} className="flex-shrink-0">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting} className="flex-grow-1">
                {submitting
                  ? "Sending…"
                  : custom
                    ? "Send request"
                    : isExtend
                      ? `Send my renewal invoice${selectedPlan && licenses > 0 ? ` — ${money(total)}` : ""}`
                      : `Send my invoice${selectedPlan ? ` — ${money(total)}` : ""}`}
              </Button>
            </div>
            <div className="plan-footnote">
              <span>Invoice within {SLA_LABEL}</span>
              {process.env.REACT_APP_WHATSAPP_URL && (
                <a href={process.env.REACT_APP_WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  Or renew on WhatsApp
                </a>
              )}
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
